import { useEffect } from 'react'

function readTargetId(allowed: ReadonlySet<string>): string | null {
  const hash = window.location.hash.replace(/^#/, '').trim()
  if (hash && allowed.has(hash)) return hash

  const path = window.location.pathname.replace(/^\//, '').split('/')[0]
  if (path && allowed.has(path)) return path

  return null
}

function scrollToId(id: string, behavior: ScrollBehavior): boolean {
  const el = document.getElementById(id)
  if (!el) return false
  el.scrollIntoView({ behavior, block: 'start' })
  return true
}

/**
 * Scrolls a SPA section into view from `/#lab` hashes and `/lab` pathnames.
 * Native hash scroll runs before React paints, so first-load anchors miss;
 * this retries after mount and after async content above the target expands.
 */
export default function useSectionDeepLink(allowedIds: readonly string[]): void {
  const allowedKey = allowedIds.join('|')

  useEffect(() => {
    const allowed = new Set(allowedKey.split('|').filter(Boolean))
    const timeouts: ReturnType<typeof setTimeout>[] = []
    let cancelled = false

    function align(behavior: ScrollBehavior): boolean {
      if (cancelled) return false
      const id = readTargetId(allowed)
      if (!id) return false
      return scrollToId(id, behavior)
    }

    function followUntilSettled(behavior: ScrollBehavior) {
      timeouts.forEach(clearTimeout)
      timeouts.length = 0
      const delays = behavior === 'auto' ? [0, 80, 250, 600, 1200] : [0, 80]
      delays.forEach((ms, i) => {
        timeouts.push(
          setTimeout(() => {
            align(i === 0 ? behavior : 'auto')
          }, ms),
        )
      })
    }

    if (readTargetId(allowed)) {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual'
      followUntilSettled('auto')
    }

    const onHashChange = () => followUntilSettled('smooth')
    window.addEventListener('hashchange', onHashChange)

    return () => {
      cancelled = true
      timeouts.forEach(clearTimeout)
      window.removeEventListener('hashchange', onHashChange)
    }
  }, [allowedKey])
}
